import { useEffect, useMemo, useState, useCallback, useLayoutEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { getCanonicalTitle } from "../../../common/util/Incentives/IncentiveRegistry";
import { useAppDispatch, useAppSelector } from "../../../store";
import { useMaxUiKpis } from "../common/hooks/useMaxUiKpis";
import { updatePeriod } from "../../../reducers/period";
import { store as accBootStore } from "../../Lit/acc-boot/acc-boot.js";
import { BonusInfoFooter } from "../../../common/components/BonusInfoFooter/BonusInfoFooter";
import { ActionReportHeader } from "../common/Header/ActionReportHeader";
import { ActionReportToolbar } from "../common/Toolbar/ActionReportToolbar";
import { ActionReportListView, ActionReportListViewLoader } from "../common/ActionReportListView/ActionReportListView";
import { ActionReportsTreeView } from "../common/ActionReportTreeView/ActionReportsTreeView";
import { KPI_ID } from "../../../services/AboEnrichment/AboEnrichmentAPI.types";
import { ERROR_TYPE, ErrorPage, IconType } from "../../ErrorPage/ErrorPage";
import { FetchWhosCloseActionReportData } from "./WhosCloseActionReportDataLayer";
import { toggleSortDirection, setAvailableKpis, setAffAbosToExpand } from "../../../reducers/whosCloseArReport";
import defaultContent from "../../../content/actionReportsWhosClose";
import LeafRectangleLoader from "../../../common/leaf/LeafLoadingSkeleton/LeafRectangleLoader/LeafRectangleLoader";
import { MODULE_WHOS_CLOSE_V2 } from "../../../common/constants/export-constants";
import ServerSideExportInProgressNotify from "../../ServerSideExport/ServerSideExportCommon/ServerSideExportNotify/ServerSideExportInProgressNotify/ServerSideExportInProgressNotify";
import ServerSideExportCompletedNotify from "../../ServerSideExport/ServerSideExportCommon/ServerSideExportNotify/ServerSideExportCompletedNotify/ServerSideExportCompletedNotify";
import { dispatchOpenExportV2ModalEvent } from "../../ServerSideExport/ServerSideExportCommon/ServerSideExportUtil/ServerSideExportEvents";
import { buildFieldTitleOverrides } from "../../../common/util/exportUtil";
import { getCurrentPeriod } from "../../../common/util/period";
import type {
  SearchSelectItem,
  SearchCallback,
  SelectionCallback,
} from "../../../common/components/SearchSelect/SearchSelect";
import {
  whosClosePageViewTrack,
  whosCloseCustomizeKpiTrack,
  whosCloseCancelCustomizeKpiTrack,
  whosCloseApplyCustomizeKpiTrack,
  whosCloseSortAscendingTrack,
  whosCloseSortDescendingTrack,
  whosCloseExportTrack,
  whosCloseProfileTrack,
  whosCloseAddToWatchlistTrack,
  whosCloseRemoveFromWatchlistTrack,
} from "../../../common/tealium/ActionReports/WhosCloseTrack";
import { CollapsibleSection } from "../common/CollapsibleSection/CollapsibleSection";
import "../ActionReports.scss";
import "./WhosCloseActionReport.scss";
import { selectGroupLeader, setSelectedLeader } from "../../../reducers/groupLeaderFilter";
import { useGroupLeaderUrlSync } from "../../../common/hooks/useGroupLeaderUrlSync";
import { GroupLeaderFilter } from "../common/GroupLeaderFilter/GroupLeaderFilter";
import { GroupLeaderFilterErrorBoundary } from "../common/GroupLeaderFilter/GroupLeaderFilterErrorBoundary";
import { leaderExistsInTree, parentLeaderOf, recordsInLeaderSubtree } from "../common/groupLeaderGrouping";
import type { RecordRef, WhosCloseABO, WhosCloseGroup } from "../../../services/ActionReports/ActionReportAPI.types";
import { getCountryNameFromCode } from "../../../common/util/formatCountryName";
import { storedLocale } from "../../Authentication/util";
import { useGetSavedKpiPreferenceQuery } from "../../../services/KPIPreferences/savedKpiPreferencesAPI";
import maskAboNumber from "../../../common/util/maskAboNumber";

const padAffSafe = (aff: string): string => {
  if (aff.length >= 3) return aff;
  return /^\d+$/.test(aff) ? aff.padStart(3, "0") : aff;
};

const DEFAULT_KPIS = [
  KPI_ID.GROUP_PV,
  KPI_ID.GROUP_PV_NEEDED,
  KPI_ID.PERSONAL_PV,
  KPI_ID.GROUP_SIZE,
  KPI_ID.FRONTLINES,
  KPI_ID.SPONSOR_NAME,
];

/**
 * Creates field configuration for KPI columns in the action report table.
 * @param kpiIds - Array of KPI identifiers from KPI_ID constants
 * @param tableColumns - Translated column labels keyed by KPI ID
 * @returns Array of field configs where:
 *   - id: Used by AboEnrichment API to fetch KPI data and by export to map fields
 *   - kpi: Used by ActionReportListView to match columns with row data
 *   - title: Displayed as column header in the table
 */
const createFieldConfig = (kpiIds: string[], tableColumns: { [key: string]: string }) =>
  kpiIds.map((kpiId) => ({
    id: kpiId,
    kpi: kpiId,
    title: tableColumns[kpiId] || kpiId,
  }));

const useSelectedKpis = (configuredKpis: string[]) => {
  const { savedKpiPreferencesData } = useAppSelector((state) => state.savedKpiPreferencesData);
  useGetSavedKpiPreferenceQuery("whosCloseKPIs", { refetchOnMountOrArgChange: true });

  return useMemo(() => {
    const savedView = savedKpiPreferencesData?.savedDataViews?.whosCloseKPIs;
    if (savedView) {
      return savedView.availableKpis
        .filter((kpi) => kpi.displaydefault && configuredKpis.includes(kpi.id))
        .map((kpi) => kpi.id);
    }
    return DEFAULT_KPIS.filter((kpi) => configuredKpis.includes(kpi));
  }, [savedKpiPreferencesData, configuredKpis]);
};

const useTreeView = (isLoading: boolean, hasData: boolean) => {
  const { hash } = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [isTreeView, setIsTreeView] = useState(hash.includes("tree") || false);

  useLayoutEffect(() => {
    if (isLoading) return;
    if (!hasData && isTreeView) {
      setIsTreeView(false);
      accBootStore.setState({ treeView: { affAbosToExpand: [] } });
      navigate("#list");
    }
  }, [isLoading, hasData, isTreeView, navigate]);

  const handleToggle = useCallback(
    (allAbos: Array<{ aff: string; abo: string }>) => {
      if (isTreeView || hash.includes("tree")) {
        setIsTreeView(false);
        accBootStore.setState({ treeView: { affAbosToExpand: [] } });
        navigate("#list");
      } else {
        const affAbosToExpand = allAbos.map((i) => `${i.aff.padStart(3, "0")}-${i.abo}`);
        dispatch(setAffAbosToExpand(affAbosToExpand));
        accBootStore.setState({
          treeView: { affAbosToExpand },
        });
        setIsTreeView(true);
        navigate("#tree");
      }
    },
    [isTreeView, hash, navigate, dispatch],
  );

  return { isTreeView, handleToggle };
};

const PeriodTableLoader = ({ count }: { count: number }) => (
  <div className="WhosCloseAR__tableWrapper">
    {Array.from({ length: count }).map((_, index) => (
      <div key={index}>
        <section className="WhosCloseAR__tableTitle">
          <LeafRectangleLoader isLoading height="1.5rem" width="8rem" margin="0" />
        </section>
        <div className="WhosCloseAR__listViewWrapper">
          <ActionReportListViewLoader />
        </div>
      </div>
    ))}
  </div>
);

export default function WhosCloseActionReport() {
  const { t } = useTranslation(["actionReportsWhosClose", "leafAboName", "actionReports"]);
  const maxUiKpis = useMaxUiKpis();
  const dispatch = useAppDispatch();
  const { configuration } = useAppSelector(({ boot }) => boot);
  const { uiKpis } = configuration;
  const enableGroupLeaderFilter = configuration.actionReports.enableGroupLeaderFilter;
  const { sortDirection, availableKpis } = useAppSelector((state) => state.whosCloseActionReportTracker);
  const confidentialUser = t("ConfidentialUser", "Confidential User", { ns: "leafAboName" });
  const selectedLeader = useAppSelector(selectGroupLeader);

  // Always use current period for Who's Close report, not the Dashboard selected period
  const currentPeriod = getCurrentPeriod();

  const isMaskAboNumber = useAppSelector((s) => s.boot.configuration.actionReports.maskConfidentialAboNumber) ?? false;

  useEffect(() => {
    dispatch(updatePeriod({ period: currentPeriod }));
  }, [dispatch, currentPeriod]);

  const whosCloseV2 = uiKpis?.whosCloseV2 || DEFAULT_KPIS;

  const { whosCloseActionReportData, isLoading, errorStatus } = FetchWhosCloseActionReportData(currentPeriod);

  const groupLeaders = useMemo(
    () => whosCloseActionReportData.groupLeaders ?? [],
    [whosCloseActionReportData.groupLeaders],
  );
  const groupLeaderTree = useMemo(
    () => whosCloseActionReportData.groupLeaderTree ?? [],
    [whosCloseActionReportData.groupLeaderTree],
  );
  // Pass the raw (possibly undefined) field so the hook defers URL hydration
  // until the leader list has actually loaded — otherwise a deep-link `?gl=`
  // can be cleared against the pre-fetch empty default.
  useGroupLeaderUrlSync(isLoading ? undefined : whosCloseActionReportData.groupLeaders);

  // Search state
  const [searchText, setSearchText] = useState<string>("");
  const [scrollToAbo, setScrollToAbo] = useState<{ aff: string; abo: string; bonusPercent: number } | undefined>(
    undefined,
  );

  // Get all ABOs for tree view
  const allAbos = useMemo(() => {
    return (whosCloseActionReportData.whosClose || []).flatMap((group) =>
      group.abos.map((abo) => ({ aff: abo.aff, abo: abo.abo })),
    );
  }, [whosCloseActionReportData.whosClose]);

  const hasData = allAbos.length > 0;
  const { isTreeView, handleToggle } = useTreeView(isLoading, hasData);

  useEffect(() => {
    if (isTreeView) {
      const affAbos = allAbos.map((i) => `${i.aff.padStart(3, "0")}-${i.abo}`);
      dispatch(setAffAbosToExpand(affAbos));
      accBootStore.setState({
        treeView: { affAbosToExpand: affAbos },
      });
    }
  }, [isTreeView, allAbos, dispatch]);

  useEffect(() => {
    dispatch(setAvailableKpis(whosCloseV2));
  }, [dispatch, whosCloseV2]);

  useEffect(() => {
    whosClosePageViewTrack(currentPeriod);
  }, [currentPeriod]);

  const handleSortClick = () => {
    if (sortDirection === "asc") {
      whosCloseSortDescendingTrack();
    } else {
      whosCloseSortAscendingTrack();
    }
    dispatch(toggleSortDirection());
  };

  const handleWatchlistTrack = (isAdding: boolean) => {
    if (isAdding) {
      whosCloseAddToWatchlistTrack();
    } else {
      whosCloseRemoveFromWatchlistTrack();
    }
  };

  const tableColumns = t("table.columns", {
    ns: "actionReportsWhosClose",
    returnObjects: true,
    defaultValue: defaultContent.table.columns,
  }) as { [key: string]: string };

  const selectedKpis = useSelectedKpis(whosCloseV2);
  // Fetch ALL available KPIs, selectedKpis only controls display
  const fieldConfig = useMemo(() => createFieldConfig(whosCloseV2, tableColumns), [whosCloseV2, tableColumns]);

  const sortedGroups = useMemo(() => {
    const groups = whosCloseActionReportData.whosClose || [];
    return sortDirection === "asc" ? [...groups].reverse() : groups;
  }, [whosCloseActionReportData.whosClose, sortDirection]);

  const getFirstGroupLeader = (a: WhosCloseABO): RecordRef | undefined => a.firstGroupLeader;
  const getSelfRef = (a: WhosCloseABO): RecordRef => ({ aff: a.aff, abo: a.abo });

  // Search pool always uses the full record set so ABOs from any leader's subtree are
  // findable. Selection auto-switches the leader filter to the found ABO's leader.
  type WhosCloseSearchEntry = {
    abo: string;
    aff: string;
    aboName: string;
    isoCountry: string;
    bonusPercent: number;
    indexInGroup: number;
    firstGroupLeader?: RecordRef;
    isConfidential: boolean;
  };

  const searchPool = useMemo<WhosCloseSearchEntry[]>(() => {
    const seen = new Set<string>();
    return sortedGroups.flatMap(({ bonusPercent, abos }) =>
      abos.reduce<WhosCloseSearchEntry[]>((acc, record, indexInGroup) => {
        const key = `${record.aff}-${record.abo}`;
        if (!seen.has(key)) {
          seen.add(key);
          acc.push({
            abo: record.abo,
            aff: record.aff,
            aboName: record.confidentialUser ? confidentialUser : record.aboName,
            isoCountry: record.isoCountry,
            bonusPercent,
            indexInGroup,
            firstGroupLeader: record.firstGroupLeader,
            isConfidential: record.confidentialUser,
          });
        }
        return acc;
      }, []),
    );
  }, [sortedGroups, confidentialUser]);

  const handleSearchCallback = useCallback<SearchCallback>(
    (text) =>
      searchPool
        .filter((entry) => entry.aboName.toLowerCase().includes(text.toLowerCase()) || entry.abo.includes(text))
        .map((entry): SearchSelectItem => {
          const sublabel = `${isMaskAboNumber && entry.isConfidential ? maskAboNumber(entry.abo) : entry.abo} | ${getCountryNameFromCode(entry.isoCountry, storedLocale())}`;
          return {
            key: `${entry.aff}-${entry.abo}-${entry.bonusPercent}`,
            label: entry.aboName,
            sublabel,
          };
        }),
    [searchPool, isMaskAboNumber],
  );

  const handleSelectionCallback = useCallback<SelectionCallback>(
    (key) => {
      const found = searchPool.find((entry) => `${entry.aff}-${entry.abo}-${entry.bonusPercent}` === key);
      if (found) {
        const selfRef: RecordRef = { aff: found.aff, abo: found.abo };
        const isInTree = leaderExistsInTree(groupLeaderTree, selfRef);
        const fglMatchesSelf =
          !!found.firstGroupLeader &&
          padAffSafe(found.firstGroupLeader.aff) === padAffSafe(found.aff) &&
          found.firstGroupLeader.abo === found.abo;
        const isSelfLeader = isInTree || fglMatchesSelf;

        let targetLeader: RecordRef | null = null;
        if (groupLeaders.length > 0) {
          if (isSelfLeader) {
            targetLeader = parentLeaderOf(groupLeaderTree, selfRef);
          } else if (found.firstGroupLeader?.aff || found.firstGroupLeader?.abo) {
            targetLeader = found.firstGroupLeader;
          }
        }

        if (groupLeaders.length > 0) {
          dispatch(setSelectedLeader(targetLeader));
        }

        setScrollToAbo({ aff: found.aff, abo: found.abo, bonusPercent: found.bonusPercent });
        setSearchText(found.aboName);
      }
      return { displayName: found?.aboName ?? "" };
    },
    [searchPool, groupLeaderTree, groupLeaders, dispatch],
  );

  const handleClearSearch = useCallback(() => {
    setSearchText("");
    setScrollToAbo(undefined);
  }, []);

  const totalAboCount = useMemo(() => {
    return sortedGroups.reduce((total, group) => total + group.abos.length, 0);
  }, [sortedGroups]);

  const exportAffAbos = useMemo(() => {
    return sortedGroups.flatMap((group) =>
      group.abos.map((record) => ({
        affAbo: `${record.aff}-${record.abo}`,
      })),
    );
  }, [sortedGroups]);

  const startedByLabels = useMemo<Record<string, string>>(
    () => ({
      [MODULE_WHOS_CLOSE_V2]: t("export.exportTypeLabel", defaultContent.export.exportTypeLabel).toString(),
    }),
    [t],
  );

  const fieldTitleOverrides = useMemo(() => buildFieldTitleOverrides(tableColumns), [tableColumns]);

  const renderBonusGroups = (groups: WhosCloseGroup[]) => (
    <div className="WhosCloseAR__tableWrapper">
      {groups.map(({ bonusPercent, abos }) => {
        if (abos.length === 0) return null;

        let scrollToIndex: number | undefined = undefined;
        if (scrollToAbo && scrollToAbo.bonusPercent === bonusPercent) {
          scrollToIndex = abos.findIndex((record) => record.aff === scrollToAbo.aff && record.abo === scrollToAbo.abo);
          if (scrollToIndex === -1) {
            scrollToIndex = undefined;
          }
        }

        const reportData = abos.map((record) => ({
          aboName: record.confidentialUser ? confidentialUser : record.aboName,
          aboNumber: record.aboNumber,
          abo: record.abo,
          aff: record.aff,
          phoneNumber: record.phoneNumber,
          whatsapp: record.whatsapp,
          level: record.level,
          imageRefUrl: record.imageRefUrl,
          isConfidential: record.confidentialUser,
        }));
        return (
          <CollapsibleSection
            key={bonusPercent}
            label={t("table.segmentTitle", defaultContent.table.segmentTitle, { segmentPercent: bonusPercent })}
            count={abos.length}
            whosCloseScrollToAbo={scrollToAbo}
            scrollIdentifier={bonusPercent}
            expandKey={selectedLeader}
            data-testid={`whos-close-group-${bonusPercent}`}
          >
            <div className="WhosCloseAR__listViewWrapper">
              <ActionReportListView
                selectedKpis={selectedKpis.filter((kpi) => availableKpis.includes(kpi))}
                reportId="whosCloseAR"
                reportData={reportData}
                fieldConfig={fieldConfig}
                enableWatchlist={true}
                scrollToIndex={scrollToIndex}
                onProfileTrack={whosCloseProfileTrack}
                onWatchlistTrack={handleWatchlistTrack}
                showImageBlock={true}
              />
            </div>
          </CollapsibleSection>
        );
      })}
    </div>
  );

  /** Re-group bonus-level groups to contain only the supplied abo set. */
  const restrictGroupsToAbos = (abos: WhosCloseABO[]): WhosCloseGroup[] => {
    const keep = new Set(abos.map((a) => `${a.aff}|${a.abo}`));
    return sortedGroups
      .map((group) => ({
        ...group,
        abos: group.abos.filter((a) => keep.has(`${a.aff}|${a.abo}`)),
      }))
      .filter((group) => group.abos.length > 0);
  };

  const renderContent = () => {
    if (isTreeView) {
      return <ActionReportsTreeView path="whosclose" />;
    }

    if (isLoading) {
      return <PeriodTableLoader count={2} />;
    }

    if (errorStatus) {
      return (
        <div className="WhosCloseAR__noDownlines">
          <ErrorPage errorType={ERROR_TYPE.GENERIC_ERROR} iconType={IconType.SearchCriteria} asMainLandmark={false} />
        </div>
      );
    }

    if (!hasData) {
      return (
        <div className="WhosCloseAR__noDownlines">
          <ErrorPage errorType={ERROR_TYPE.NO_DOWNLINES} iconType={IconType.SearchCriteria} asMainLandmark={false} />
        </div>
      );
    }

    const groupAbos: WhosCloseABO[] = sortedGroups.flatMap((g) => g.abos);

    if (selectedLeader && groupLeaders.length > 0) {
      const scoped = recordsInLeaderSubtree(
        groupAbos,
        groupLeaderTree,
        selectedLeader,
        getSelfRef,
        getFirstGroupLeader,
      );
      if (scoped.length === 0) {
        return (
          <div className="WhosCloseAR__noDownlines">
            <ErrorPage errorType={ERROR_TYPE.NO_DOWNLINES} iconType={IconType.SearchCriteria} asMainLandmark={false} />
          </div>
        );
      }
      return renderBonusGroups(restrictGroupsToAbos(scoped));
    }

    return renderBonusGroups(sortedGroups);
  };

  const pageTitle = getCanonicalTitle({ id: "whosClose" }, t, {});

  return (
    <main className="WhosCloseAR">
      <ActionReportHeader
        title={pageTitle}
        description={t("title.pageDescription", defaultContent.title.pageDescription).toString()}
        reportLength={totalAboCount}
      />
      <ActionReportToolbar
        reportLength={totalAboCount}
        isAllEnriched={true}
        treeViewToggle={{
          enabled: !configuration.actionReports.disableTreeView,
          isTreeView: isTreeView,
          onToggle: () => handleToggle(allAbos),
          disabled: !hasData,
          label: t("losTreeView", defaultContent.losTreeView).toString(),
        }}
        sort={{
          enabled: !isTreeView,
          direction: sortDirection,
          onClick: handleSortClick,
          disabled: isLoading || !hasData,
        }}
        kpiSelector={
          !isTreeView
            ? {
                availableKpis: whosCloseV2,
                defaultKpis: DEFAULT_KPIS,
                saveViewName: "whosCloseKPIs",
                saveViewDescription: "Who's Close KPI Preferences",
                maxSelectionsAllowed: maxUiKpis,
                onOpenTrack: whosCloseCustomizeKpiTrack,
                onCancelTrack: whosCloseCancelCustomizeKpiTrack,
                onApplyTrack: whosCloseApplyCustomizeKpiTrack,
                disabled: isLoading || !hasData,
              }
            : undefined
        }
        search={
          !isTreeView
            ? {
                enabled: true,
                searchCallback: handleSearchCallback,
                selectionCallback: handleSelectionCallback,
                searchText: searchText,
                clearFunction: handleClearSearch,
                disabled: isLoading || searchPool.length === 0,
              }
            : undefined
        }
        export={{
          moduleId: MODULE_WHOS_CLOSE_V2,
          exportAffAbos: exportAffAbos,
          selectedKpis: selectedKpis.filter((kpi) => whosCloseV2.includes(kpi)),
          confirmationMessage: t("export.description", defaultContent.export.description).toString(),
          exportType: MODULE_WHOS_CLOSE_V2,
          startedByLabels,
          fieldTitleOverrides,
        }}
        exportTrack={whosCloseExportTrack}
        groupLeaderFilterSlot={
          enableGroupLeaderFilter ? (
            isLoading ? (
              <GroupLeaderFilter leaders={[]} tree={[]} isLoading />
            ) : groupLeaders.length > 0 ? (
              <GroupLeaderFilterErrorBoundary>
                <GroupLeaderFilter
                  leaders={groupLeaders}
                  tree={groupLeaderTree}
                  allOptionLabel={t(
                    "filter.groupLeader.allOption",
                    defaultContent.filter.groupLeader.allOption,
                  ).toString()}
                />
              </GroupLeaderFilterErrorBoundary>
            ) : undefined
          ) : undefined
        }
      />
      <div className="WhosCloseAR__content">{renderContent()}</div>
      <div>
        <BonusInfoFooter />
      </div>
      <ServerSideExportInProgressNotify />
      <ServerSideExportCompletedNotify
        moduleId={MODULE_WHOS_CLOSE_V2}
        notifyDownloadLinkOnClick={() => dispatchOpenExportV2ModalEvent(MODULE_WHOS_CLOSE_V2, true)}
      />
    </main>
  );
}
